import { useState, useCallback, useRef, useEffect } from 'react';
import { ChunkUploadManager } from '../../services/chunkUploadService';
import uploadPersistenceService from '../../services/uploadPersistenceService';
import {
    UploadProgressInfo,
    StartUploadFunc,
    UploadConfig,
    PauseUploadFunc,
    ResumeUploadFunc,
    CancelUploadFunc,
    CloseUploadProgressFunc
} from './types';
import { useSnackbar } from '../SnackbarProvider';
import { SnackbarType } from '../../components/Snackbar/types';

interface QueuedUploadTask {
    id: string;
    file: File;
    title: string;
    config: UploadConfig;
    uploadManager: ChunkUploadManager;
}

const MAX_VISIBLE_UPLOADS = 5;

const isTerminalStatus = (status: UploadProgressInfo['status']): boolean => {
    return ['completed', 'error', 'cancelled'].includes(status);
};

const rebalanceUploadVisibility = (progresses: UploadProgressInfo[], maxVisible: number): UploadProgressInfo[] => {
    const next = progresses.map(progress => ({ ...progress }));
    const openIndexes: number[] = [];

    next.forEach((progress, index) => {
        if (progress.open) {
            openIndexes.push(index);
        }
    });

    let openCount = openIndexes.length;
    if (openCount > maxVisible) {
        let toClose = openCount - maxVisible;
        const closeOrder = [
            ...openIndexes.filter(index => isTerminalStatus(next[index].status)),
            ...openIndexes.filter(index => !isTerminalStatus(next[index].status)),
        ];

        for (const index of closeOrder) {
            if (toClose <= 0) break;
            if (!next[index].open) continue;
            next[index] = {
                ...next[index],
                open: false,
                hiddenReason: 'auto',
            };
            toClose -= 1;
        }
        openCount = maxVisible;
    }

    if (openCount < maxVisible) {
        let toOpen = maxVisible - openCount;
        const hiddenAutoActive: number[] = [];
        const hiddenAutoTerminal: number[] = [];

        next.forEach((progress, index) => {
            if (!progress.open && progress.hiddenReason === 'auto') {
                if (isTerminalStatus(progress.status)) {
                    hiddenAutoTerminal.push(index);
                } else {
                    hiddenAutoActive.push(index);
                }
            }
        });

        const openOrder = [...hiddenAutoActive, ...hiddenAutoTerminal];
        for (const index of openOrder) {
            if (toOpen <= 0) break;
            next[index] = { ...next[index], open: true, hiddenReason: undefined };
            toOpen -= 1;
        }
    }

    return next;
};

export const useUploadProgressProvider = () => {
    const [uploadProgresses, setUploadProgresses] = useState<UploadProgressInfo[]>([]);
    const showSnackbar = useSnackbar();

    // コンポーネントのマウント状態を追跡
    const isMountedRef = useRef<boolean>(true);
    const uploadProgressesRef = useRef<UploadProgressInfo[]>([]);
    const queuedUploadTasksRef = useRef<QueuedUploadTask[]>([]);
    const isProcessingQueueRef = useRef<boolean>(false);
    const cancelledTaskIdsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        isMountedRef.current = true;

        // IndexedDB初期化
        uploadPersistenceService.init().catch((error) => {
            console.error('IndexedDB初期化エラー:', error);
        });

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        uploadProgressesRef.current = uploadProgresses;
    }, [uploadProgresses]);

    const setProgresses = useCallback((
        updater: UploadProgressInfo[] | ((prev: UploadProgressInfo[]) => UploadProgressInfo[])
    ) => {
        setUploadProgresses(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            return rebalanceUploadVisibility(next, MAX_VISIBLE_UPLOADS);
        });
    }, []);

    const getProgressById = useCallback((id: string) => {
        return uploadProgressesRef.current.find(p => p.id === id);
    }, []);

    const executeUploadTask = useCallback(async (task: QueuedUploadTask): Promise<void> => {
        const { uploadManager, file, config, id: tempId } = task;
        let currentId = tempId;

        // キュー待機中にキャンセルされたタスクは処理しない
        if (cancelledTaskIdsRef.current.has(tempId)) {
            cancelledTaskIdsRef.current.delete(tempId);
            return;
        }

        // イベントハンドラーを登録
        uploadManager.on('progress', (progress: number, uploadedChunks: number[]) => {
            if (!isMountedRef.current) return;

            setProgresses(prev => prev.map(p => {
                if (p.id === currentId) {
                    return {
                        ...p,
                        progress,
                        status: 'uploading'
                    };
                }
                return p;
            }));

            const sessionId = uploadManager.getSessionId();
            if (sessionId) {
                uploadPersistenceService.updateProgress(sessionId, uploadedChunks).catch(console.error);
            }
        });

        uploadManager.on('error', (error: Error) => {
            if (!isMountedRef.current) return;

            console.error('アップロードエラー:', error);
            setProgresses(prev => prev.map(p => {
                if (p.id === currentId) {
                    return {
                        ...p,
                        status: 'error',
                        error: error.message
                    };
                }
                return p;
            }));
        });

        uploadManager.on('complete', (videoId: string) => {
            if (!isMountedRef.current) return;

            // アップロード完了
            const sessionId = uploadManager.getSessionId();
            const resolvedVideoId = videoId || undefined;
            setProgresses(prev => prev.map(p => {
                if (p.id === currentId) {
                    return {
                        ...p,
                        progress: 100,
                        status: 'completed',
                        videoId: resolvedVideoId
                    };
                }
                return p;
            }));

            if (sessionId) {
                uploadPersistenceService.deleteSession(sessionId).catch(console.error);
            }
        });

        uploadManager.on('paused', () => {
            if (!isMountedRef.current) return;

            setProgresses(prev => prev.map(p => {
                if (p.id === currentId) {
                    return { ...p, status: 'paused' };
                }
                return p;
            }));
        });

        uploadManager.on('cancelled', () => {
            if (!isMountedRef.current) return;

            setProgresses(prev => prev.map(p => {
                if (p.id === currentId) {
                    return { ...p, status: 'cancelled', progress: 0, hiddenReason: undefined };
                }
                return p;
            }));

            const sessionId = uploadManager.getSessionId();
            if (sessionId) {
                uploadPersistenceService.deleteSession(sessionId).catch(console.error);
            }
        });

        const uploadMetadata = {
            title: config.title,
            description: config.description,
            visibility: config.visibility,
        };

        try {
            let resumeResult: {
                sessionId: string;
                uploadedChunks: number[];
                totalChunks: number;
                chunkSize: number;
            } | null = null;

            if (config.canResume) {
                try {
                    resumeResult = await uploadManager.resumeSession(file, uploadMetadata);
                } catch (resumeError) {
                    console.warn('セッション再開に失敗したため新規セッションを作成します:', resumeError);
                    resumeResult = null;
                }
            }

            if (!resumeResult) {
                await uploadManager.initialize(file, uploadMetadata);
            }

            if (cancelledTaskIdsRef.current.has(tempId)) {
                cancelledTaskIdsRef.current.delete(tempId);
                return;
            }

            const sessionId = uploadManager.getSessionId();
            if (sessionId) {
                currentId = sessionId;
                const resumedProgress = resumeResult && resumeResult.totalChunks > 0
                    ? Math.round((resumeResult.uploadedChunks.length / resumeResult.totalChunks) * 100)
                    : undefined;

                setProgresses(prev => prev.map(p => {
                    if (p.id === tempId) {
                        return {
                            ...p,
                            id: sessionId,
                            sessionId,
                            hiddenReason: p.hiddenReason,
                            ...(typeof resumedProgress === 'number' ? { progress: resumedProgress } : {}),
                        };
                    }
                    return p;
                }));

                await uploadPersistenceService.saveSession({
                    sessionId,
                    fileName: file.name,
                    fileSize: file.size,
                    fileHash: uploadManager.getFileHash() || undefined,
                    totalChunks: uploadManager.getTotalChunks(),
                    uploadedChunks: uploadManager.getUploadedChunks(),
                    metadata: uploadMetadata,
                    createdAt: Date.now(),
                    lastUpdatedAt: Date.now(),
                });
            }

            if (cancelledTaskIdsRef.current.has(tempId) || cancelledTaskIdsRef.current.has(currentId)) {
                cancelledTaskIdsRef.current.delete(tempId);
                cancelledTaskIdsRef.current.delete(currentId);
                return;
            }

            await uploadManager.start();
        } catch (error) {
            const message = (error as Error).message;
            if (message === 'Upload cancelled' || message === 'アップロードがキャンセルされました') {
                return;
            }

            console.error('アップロード開始エラー:', error);
            setProgresses(prev => prev.map(p => {
                if (p.id === currentId) {
                    return {
                        ...p,
                        status: 'error',
                        error: message
                    };
                }
                return p;
            }));
        }
    }, [setProgresses, showSnackbar]);

    const processUploadQueue = useCallback(async (): Promise<void> => {
        if (isProcessingQueueRef.current) {
            return;
        }

        isProcessingQueueRef.current = true;

        try {
            while (queuedUploadTasksRef.current.length > 0) {
                const task = queuedUploadTasksRef.current.shift();
                if (!task) continue;
                await executeUploadTask(task);
            }
        } finally {
            isProcessingQueueRef.current = false;

            // ループ終了後に追加されたタスクを取りこぼさない
            if (queuedUploadTasksRef.current.length > 0) {
                void processUploadQueue();
            }
        }
    }, [executeUploadTask]);

    const startUpload: StartUploadFunc = useCallback(async (configs: UploadConfig[]) => {
        const newProgresses = configs.map(config => {
            const tempId = `upload-${config.file.name}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
            const uploadManager = new ChunkUploadManager();

            return {
                clientId: tempId,
                id: tempId,
                file: config.file,
                fileName: config.file.name,
                title: config.title,
                progress: 0,
                status: 'pending' as const,
                uploadManager,
                open: true,
            };
        });

        setProgresses(prev => [...prev, ...newProgresses]);

        const queuedTasks = newProgresses.map((progressInfo, index) => ({
            id: progressInfo.id,
            file: progressInfo.file,
            title: progressInfo.title,
            config: configs[index],
            uploadManager: progressInfo.uploadManager,
        }));

        queuedUploadTasksRef.current.push(...queuedTasks);
        void processUploadQueue();
    }, [processUploadQueue, setProgresses]);

    const pauseUpload: PauseUploadFunc = useCallback((id: string) => {
        const progress = getProgressById(id);
        if (progress && progress.uploadManager) {
            progress.uploadManager.pause();
        }
    }, [getProgressById, setProgresses]);

    const resumeUpload: ResumeUploadFunc = useCallback(async (id: string) => {
        const progress = getProgressById(id);
        if (progress && progress.uploadManager) {
            try {
                await progress.uploadManager.resume();
            } catch (error) {
                console.error('再開エラー:', error);
                showSnackbar('アップロードの再開に失敗しました', SnackbarType.ERROR);
            }
        }
    }, [getProgressById, showSnackbar]);

    const cancelUpload: CancelUploadFunc = useCallback((id: string) => {
        const progress = getProgressById(id);
        if (progress && progress.uploadManager) {
            if (progress.status === 'pending') {
                const queueIndex = queuedUploadTasksRef.current.findIndex(task => task.id === id);
                if (queueIndex >= 0) {
                    queuedUploadTasksRef.current.splice(queueIndex, 1);
                    cancelledTaskIdsRef.current.delete(id);
                } else {
                    cancelledTaskIdsRef.current.add(id);
                }
                setProgresses(prev => prev.map(p => {
                    if (p.id === id) {
                        return {
                            ...p,
                            status: 'cancelled',
                            progress: 0,
                            open: true,
                            hiddenReason: undefined,
                        };
                    }
                    return p;
                }));
                progress.uploadManager.cancel();
                return;
            }
            progress.uploadManager.cancel();
            // キャンセル済み状態はイベントハンドラで更新されるが、
            // UI上ですぐに消えるか残るかは要件次第。
            // DownloadProgress同様、ユーザーが閉じるまでは残す（status='cancelled'で表示）
        }
    }, [getProgressById]);

    const closeUploadProgress: CloseUploadProgressFunc = useCallback((id: string) => {
        // 完了、エラー、キャンセル済みの場合はリストから削除
        // アップロード中の場合は非表示にするだけ（バックグラウンド継続）

        setProgresses(prev => {
            const target = prev.find(p => p.id === id);
            if (!target) return prev;

            if (['completed', 'error', 'cancelled'].includes(target.status)) {
                return prev.filter(p => p.id !== id);
            } else {
                // アップロード中は非表示にするだけ
                return prev.map(p => p.id === id ? { ...p, open: false, hiddenReason: 'user' } : p);
            }
        });
    }, [setProgresses]);

    return {
        startUpload,
        pauseUpload,
        resumeUpload,
        cancelUpload,
        closeUploadProgress,
        uploadProgresses
    };
};
